import React, { useState, useEffect } from 'react';
import { Modal } from "../ui/modal";
import Swal from 'sweetalert2';
import { client } from '../../sdkAdapter';
import { buildApiUrl, getAuthHeaders } from '../../utils/apiBase';
import MediaPickerModal from '../common/MediaPickerModal';

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const getFullUrl = (url?: string) => {
    if (!url) return undefined;
    if (url.startsWith('http')) return url;
    const base = (import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000').replace(/\/$/, '');
    return `${base}/${url.replace(/^\//, '')}`;
};

const isModeActive = (m: any) =>
    m.active === true || m.active === 1 || m.active === '1' ||
    m.is_active === true || m.is_active === 1 || m.is_active === '1';

/* ─── icons ───────────────────────────────────────────────────────────────── */

function CodIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
    );
}

function CardIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
    );
}

function BankIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
        </svg>
    );
}

function OtherIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );
}

function GatewayIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
    );
}

function SpinnerIcon() {
    return (
        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
    );
}

/* ─── categorisation ──────────────────────────────────────────────────────── */

type Category = 'cod' | 'credit' | 'bank' | 'other';

function categorise(mode: any): Category {
    const s = ((mode.slug || '') + ' ' + (mode.name || mode.label || '')).toLowerCase();
    if (s.includes('cash') || s.includes('cheque') || s.includes('cod') ||
        (s.includes('delivery') && !s.includes('bank'))) return 'cod';
    if (s.includes('credit') || s.includes('card')) return 'credit';
    if (s.includes('bank') || s.includes('transfer')) return 'bank';
    return 'other';
}

interface CategoryMeta {
    label: string;
    description: string;
    Icon: () => React.ReactNode;
    colorClass: string;
}

function getCategoryMeta(cat: Category | 'gateway'): CategoryMeta {
    switch (cat) {
        case 'cod':
            return { label: 'Cash Payments', description: 'Cash and cheque payment methods', Icon: CodIcon, colorClass: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' };
        case 'credit':
            return { label: 'Card Payments', description: 'Credit and debit card methods', Icon: CardIcon, colorClass: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400' };
        case 'bank':
            return { label: 'Bank Transfer', description: 'Direct bank transfer methods', Icon: BankIcon, colorClass: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400' };
        case 'gateway':
            return { label: 'Online Gateway', description: 'Online payment gateway', Icon: GatewayIcon, colorClass: 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400' };
        default:
            return { label: 'Other', description: 'Additional payment options', Icon: OtherIcon, colorClass: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' };
    }
}

/* ─── empty form ──────────────────────────────────────────────────────────── */

const EMPTY_FORM = {
    name: '', label: '', description: '', slug: '',
    image_id: 0 as number | null,
    image_preview: '',
    is_active: true,
};

/* ─── Main component ──────────────────────────────────────────────────────── */

export default function SettingsPaymentGateways() {
    // ── Data ──
    const [gateways, setGateways] = useState<any[]>([]);
    const [paymentModes, setPaymentModes] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // ── Modal ──
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalTab, setModalTab] = useState<'configure' | 'manage'>('configure');

    // ── Configure tab ──
    const [selectedGatewayId, setSelectedGatewayId] = useState<number | null>(null);
    const [checkedModeIds, setCheckedModeIds] = useState<Set<number>>(new Set());
    const [configSaving, setConfigSaving] = useState(false);

    // ── Manage Modes tab ──
    const [editingModeId, setEditingModeId] = useState<number | 'new' | null>(null);
    const [modeForm, setModeForm] = useState({ ...EMPTY_FORM });
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    const [modeSaving, setModeSaving] = useState(false);

    /* ── fetch ── */
    const refreshData = async () => {
        setLoading(true);
        try {
            const [gwRes, pmRes] = await Promise.all([
                fetch(buildApiUrl('/settings/online-payment/gateways'), {
                    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
                }),
                client.getApiSettingsPaymentModes() as any,
            ]);

            const gwData = gwRes.ok ? await gwRes.json() : { data: [] };
            setGateways(Array.isArray(gwData.data) ? gwData.data : []);

            const pmData = pmRes.data?.data?.data || pmRes.data?.data || pmRes.data || [];
            setPaymentModes(Array.isArray(pmData) ? pmData : []);
        } catch (err) {
            console.error('Failed to fetch payment data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refreshData(); }, []);

    /* ── open modal ── */
    const openModal = (tab: 'configure' | 'manage' = 'configure') => {
        const activeGw = gateways.find((g) => g.active);
        setSelectedGatewayId(activeGw?.id ?? null);
        setCheckedModeIds(new Set(paymentModes.filter(isModeActive).map((m: any) => m.id as number)));
        setEditingModeId(null);
        setModeForm({ ...EMPTY_FORM });
        setModalTab(tab);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingModeId(null);
        setModeForm({ ...EMPTY_FORM });
    };

    /* ── toggle mode checkbox ── */
    const toggleMode = (id: number) => {
        setCheckedModeIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    /* ── save configuration ── */
    const handleSaveConfig = async () => {
        setConfigSaving(true);
        try {
            const promises: Promise<any>[] = [];

            if (selectedGatewayId) {
                promises.push(
                    fetch(buildApiUrl(`/settings/online-payment/gateway/activate/${selectedGatewayId}`), {
                        method: 'POST',
                        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
                        body: JSON.stringify({ active: true }),
                    })
                );
            }

            for (const mode of paymentModes) {
                const wasActive = isModeActive(mode);
                const nowActive = checkedModeIds.has(mode.id);
                if (wasActive !== nowActive) {
                    promises.push(
                        (client as any).postApiSettingsPaymentModeUpdate({
                            postApiSettingsPaymentModeUpdateRequest: {
                                id: mode.id, name: mode.name, label: mode.label,
                                description: mode.description || '', slug: mode.slug,
                                active: nowActive,
                            },
                        })
                    );
                }
            }

            await Promise.all(promises);
            closeModal();
            await refreshData();
            Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Configuration saved', showConfirmButton: false, timer: 2000, timerProgressBar: true });
        } catch {
            Swal.fire({ title: 'Error', text: 'Failed to save configuration', icon: 'error' });
        } finally {
            setConfigSaving(false);
        }
    };

    /* ── mode form ── */
    const handleModeFormChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setModeForm((prev) => ({
            ...prev,
            [name]: name === 'is_active' ? value === '1' : value,
        }));
    };

    const handleEditMode = async (item: any) => {
        setEditingModeId(item.id);
        setModeForm({
            name: item.name || '', label: item.label || '', description: item.description || '',
            slug: item.slug || '', image_id: item.image_id || 0,
            image_preview: item.image?.path || item.image?.url || '',
            is_active: isModeActive(item),
        });
        try {
            const res = await (client as any).getApiSettingsPaymentModeId({ id: item.id }) as any;
            let data = res.data?.data || res.data;
            if (Array.isArray(data)) data = data[0];
            if (data) {
                setModeForm({
                    name: data.name || '', label: data.label || '', description: data.description || '',
                    slug: data.slug || '', image_id: data.image_id || 0,
                    image_preview: data.image?.path || data.image?.url || '',
                    is_active: data.active !== undefined
                        ? (typeof data.active === 'string' ? data.active === '1' : !!data.active)
                        : (data.is_active ?? true),
                });
            }
        } catch { /* fall back to list data already set */ }
    };

    const handleSaveMode = async () => {
        if (!modeForm.name || !modeForm.label) return;
        setModeSaving(true);
        try {
            const payload: any = {
                name: modeForm.name, label: modeForm.label,
                description: modeForm.description, slug: modeForm.slug,
                active: modeForm.is_active,
            };
            if (modeForm.image_id) payload.image_id = modeForm.image_id;

            if (editingModeId && editingModeId !== 'new') {
                await (client as any).postApiSettingsPaymentModeUpdate({
                    postApiSettingsPaymentModeUpdateRequest: { ...payload, id: editingModeId },
                });
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Mode updated', showConfirmButton: false, timer: 2000, timerProgressBar: true });
            } else {
                await (client as any).postApiSettingsPaymentModeCreate({
                    postApiSettingsPaymentModeCreateRequest: payload,
                });
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Mode created', showConfirmButton: false, timer: 2000, timerProgressBar: true });
            }

            setEditingModeId(null);
            setModeForm({ ...EMPTY_FORM });
            await refreshData();
        } catch (err: any) {
            const msg = err?.response?.data?.messages?.message || 'Failed to save mode';
            Swal.fire({ icon: 'error', title: 'Error', text: typeof msg === 'string' ? msg : 'An error occurred' });
        } finally {
            setModeSaving(false);
        }
    };

    const handleDeleteMode = async (id: number) => {
        const result = await Swal.fire({
            title: 'Delete this mode?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
        });
        if (result.isConfirmed) {
            try {
                await (client as any).postApiSettingsPaymentModeDelete({
                    postApiSettingsPaymentModeDeleteRequest: { id },
                });
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Mode deleted', showConfirmButton: false, timer: 2000, timerProgressBar: true });
                if (editingModeId === id) { setEditingModeId(null); setModeForm({ ...EMPTY_FORM }); }
                await refreshData();
            } catch {
                Swal.fire({ title: 'Error', text: 'Failed to delete mode', icon: 'error' });
            }
        }
    };

    /* ── derived data ── */
    const groupedModes = paymentModes.reduce<Record<string, any[]>>((acc, mode) => {
        const cat = categorise(mode);
        (acc[cat] = acc[cat] || []).push(mode);
        return acc;
    }, {});

    const activeGateways = gateways.filter((g) => g.active);
    const activePaymentModes = paymentModes.filter(isModeActive);
    const totalActive = activeGateways.length + activePaymentModes.length;

    /* ── render ── */
    return (
        <>
            {/* ── Main Card ── */}
            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">Active Payment Options</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Payment gateways and modes currently available to customers.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {loading && (
                            <span className="text-sm text-gray-400 flex items-center gap-1.5">
                                <SpinnerIcon /> Loading…
                            </span>
                        )}
                        <button
                            onClick={() => openModal('manage')}
                            className="px-3 py-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm font-medium flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h7" />
                            </svg>
                            Manage Modes
                        </button>
                        <button
                            onClick={() => openModal('configure')}
                            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm font-medium flex items-center gap-2 shadow-sm shadow-brand-500/20"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Configure
                        </button>
                    </div>
                </div>

                {/* Active Options — Card Grid */}
                <div className="p-5">
                    {loading && totalActive === 0 ? (
                        /* Skeleton */
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="h-24 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
                            ))}
                        </div>
                    ) : totalActive === 0 ? (
                        /* Empty state */
                        <div className="flex flex-col items-center justify-center py-14 text-center">
                            <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
                                <svg className="w-7 h-7 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                            </div>
                            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">No active payment options</p>
                            <p className="text-xs text-gray-400 mt-1 mb-4">Click <span className="font-medium">Configure</span> to enable payment methods for your customers.</p>
                            <button
                                onClick={() => openModal('configure')}
                                className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm font-medium shadow-sm shadow-brand-500/20"
                            >
                                Configure Now
                            </button>
                        </div>
                    ) : (
                        /* Payment method cards */
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {activeGateways.map((gw) => {
                                const { Icon, colorClass } = getCategoryMeta('gateway');
                                return (
                                    <PaymentCard
                                        key={`gw-${gw.id}`}
                                        name={gw.label || gw.name}
                                        type="Gateway"
                                        colorClass={colorClass}
                                        icon={<Icon />}
                                    />
                                );
                            })}
                            {activePaymentModes.map((mode) => {
                                const cat = categorise(mode);
                                const { Icon, colorClass } = getCategoryMeta(cat);
                                return (
                                    <PaymentCard
                                        key={`mode-${mode.id}`}
                                        name={mode.label || mode.name}
                                        type="Mode"
                                        colorClass={colorClass}
                                        icon={
                                            mode.image
                                                ? <img src={getFullUrl(mode.image.path || mode.image.url)} alt={mode.name} className="w-5 h-5 object-contain" />
                                                : <Icon />
                                        }
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>

                {totalActive > 0 && (
                    <div className="px-5 pb-4 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                        <p className="text-xs text-gray-400">
                            {totalActive} payment option{totalActive !== 1 ? 's' : ''} active
                        </p>
                    </div>
                )}
            </div>

            {/* ── Unified Modal ── */}
            <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                className="max-w-2xl p-0 overflow-hidden"
            >
                <div className="flex flex-col h-full max-h-[90vh]">
                    {/* Modal header + tabs */}
                    <div className="px-6 pt-6 pb-0 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-10">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">Payment Options</h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {modalTab === 'configure'
                                ? 'Choose which gateways and payment modes are available to customers.'
                                : 'Add, edit, or delete payment modes in your registry.'}
                        </p>
                        {/* Tab switcher */}
                        <div className="flex gap-0 mt-4 -mb-px">
                            <ModalTab active={modalTab === 'configure'} onClick={() => setModalTab('configure')}>
                                Configure
                            </ModalTab>
                            <ModalTab active={modalTab === 'manage'} onClick={() => setModalTab('manage')}>
                                Manage Modes
                                <span className={`ml-2 text-xs font-bold px-1.5 py-0.5 rounded-full ${modalTab === 'manage' ? 'bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>
                                    {paymentModes.length}
                                </span>
                            </ModalTab>
                        </div>
                    </div>

                    {/* ── Configure Tab ── */}
                    {modalTab === 'configure' && (
                        <div className="p-6 overflow-y-auto flex-grow space-y-5">
                            {/* Gateway */}
                            <SectionCard
                                icon={<GatewayIcon />}
                                title="Online Payment Gateway"
                                description="Only one gateway can be active at a time"
                                accent="brand"
                            >
                                {gateways.length === 0 ? (
                                    <p className="text-sm text-gray-400 italic">No gateways available.</p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {gateways.map((gw) => (
                                            <label
                                                key={gw.id}
                                                htmlFor={`cfg-gw-${gw.id}`}
                                                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${selectedGatewayId === gw.id
                                                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                    }`}
                                            >
                                                <input
                                                    type="radio"
                                                    id={`cfg-gw-${gw.id}`}
                                                    name="config_gateway"
                                                    checked={selectedGatewayId === gw.id}
                                                    onChange={() => setSelectedGatewayId(gw.id)}
                                                    className="w-4 h-4 accent-brand-500 shrink-0"
                                                />
                                                <span className="font-medium text-gray-700 dark:text-gray-200 truncate">{gw.label}</span>
                                                {gw.active && (
                                                    <span className="ml-auto shrink-0 text-xs font-semibold text-green-700 bg-green-100 dark:bg-green-500/20 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                                                        Current
                                                    </span>
                                                )}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </SectionCard>

                            {/* Mode sections by category */}
                            {(['cod', 'credit', 'bank', 'other'] as Category[]).map((catKey) => {
                                const modes = groupedModes[catKey] || [];
                                if (modes.length === 0) return null;
                                const { label, description, Icon } = getCategoryMeta(catKey);
                                return (
                                    <SectionCard key={catKey} icon={<Icon />} title={label} description={description} accent="gray">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {modes.map((mode: any) => (
                                                <label
                                                    key={mode.id}
                                                    htmlFor={`cfg-mode-${mode.id}`}
                                                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${checkedModeIds.has(mode.id)
                                                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                                                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                        }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        id={`cfg-mode-${mode.id}`}
                                                        checked={checkedModeIds.has(mode.id)}
                                                        onChange={() => toggleMode(mode.id)}
                                                        className="w-4 h-4 accent-brand-500 shrink-0 rounded"
                                                    />
                                                    <span className="font-medium text-gray-700 dark:text-gray-200">{mode.label || mode.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </SectionCard>
                                );
                            })}
                        </div>
                    )}

                    {/* ── Manage Modes Tab ── */}
                    {modalTab === 'manage' && (
                        <div className="p-6 overflow-y-auto flex-grow space-y-4">
                            {/* Inline Add / Edit Form */}
                            {editingModeId !== null && (
                                <div className="rounded-xl border border-brand-200 dark:border-brand-500/30 bg-brand-50/40 dark:bg-brand-500/5 overflow-hidden">
                                    <div className="px-4 py-3 border-b border-brand-200 dark:border-brand-500/30 flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-md bg-brand-100 dark:bg-brand-500/20 text-brand-600 dark:text-brand-400 flex items-center justify-center">
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                {editingModeId === 'new'
                                                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                }
                                            </svg>
                                        </div>
                                        <p className="text-sm font-semibold text-gray-800 dark:text-white">
                                            {editingModeId === 'new' ? 'Add New Mode' : 'Edit Mode'}
                                        </p>
                                    </div>
                                    <div className="p-4 space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                                                    Name (Internal) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text" name="name" value={modeForm.name}
                                                    onChange={handleModeFormChange}
                                                    placeholder="e.g. cash_on_delivery"
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-800 outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                                                    Label (Display) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text" name="label" value={modeForm.label}
                                                    onChange={handleModeFormChange}
                                                    placeholder="e.g. Cash on Delivery"
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-800 outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-colors"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Slug</label>
                                                <input
                                                    type="text" name="slug" value={modeForm.slug}
                                                    onChange={handleModeFormChange}
                                                    placeholder="e.g. cash-on-delivery"
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-800 outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 font-mono transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Status</label>
                                                <select
                                                    name="is_active" value={modeForm.is_active ? '1' : '0'}
                                                    onChange={handleModeFormChange as any}
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-800 outline-none focus:ring-2 focus:ring-brand-500/20 transition-colors"
                                                >
                                                    <option value="1">Active</option>
                                                    <option value="0">Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Description</label>
                                            <textarea
                                                name="description" value={modeForm.description}
                                                onChange={handleModeFormChange} rows={2}
                                                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-800 outline-none focus:ring-2 focus:ring-brand-500/20 resize-none transition-colors"
                                            />
                                        </div>
                                        {/* Image picker */}
                                        <div className="flex items-center gap-3">
                                            <div className="relative w-11 h-11 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-black/20 group shrink-0">
                                                {modeForm.image_preview ? (
                                                    <>
                                                        <img src={getFullUrl(modeForm.image_preview)} alt="Preview" className="w-full h-full object-cover" />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <button
                                                                type="button"
                                                                onClick={() => setModeForm((p) => ({ ...p, image_id: 0, image_preview: '' }))}
                                                                className="p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                                                            >
                                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setIsMediaModalOpen(true)}
                                                className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-400"
                                            >
                                                {modeForm.image_preview ? 'Change Image' : 'Select Image'}
                                            </button>
                                        </div>
                                        {/* Form actions */}
                                        <div className="flex justify-end gap-2 pt-1">
                                            <button
                                                onClick={() => { setEditingModeId(null); setModeForm({ ...EMPTY_FORM }); }}
                                                className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleSaveMode}
                                                disabled={modeSaving || !modeForm.name || !modeForm.label}
                                                className="px-4 py-1.5 text-xs text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-sm shadow-brand-500/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                                            >
                                                {modeSaving ? <><SpinnerIcon />Saving…</> : (editingModeId === 'new' ? 'Create Mode' : 'Save Changes')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Modes list */}
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-white/[0.02] flex items-center justify-between">
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">All Modes</p>
                                    {editingModeId === null && (
                                        <button
                                            onClick={() => { setEditingModeId('new'); setModeForm({ ...EMPTY_FORM }); }}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-xs font-semibold transition-colors shadow-sm shadow-brand-500/20"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            Add New Mode
                                        </button>
                                    )}
                                </div>
                                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                    {paymentModes.length === 0 ? (
                                        <div className="py-10 text-center text-sm text-gray-400">
                                            {loading ? 'Loading…' : 'No payment modes found. Add one above.'}
                                        </div>
                                    ) : (
                                        paymentModes.map((item) => {
                                            const cat = categorise(item);
                                            const { Icon, colorClass } = getCategoryMeta(cat);
                                            const active = isModeActive(item);
                                            const isBeingEdited = editingModeId === item.id;
                                            return (
                                                <div
                                                    key={item.id}
                                                    className={`flex items-center gap-3 px-4 py-3 transition-colors ${isBeingEdited
                                                        ? 'bg-brand-50/40 dark:bg-brand-500/5'
                                                        : 'hover:bg-gray-50/60 dark:hover:bg-white/[0.01]'
                                                        }`}
                                                >
                                                    {/* Icon */}
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                                                        {item.image
                                                            ? <img src={getFullUrl(item.image.path || item.image.url)} alt={item.name} className="w-4 h-4 object-contain" />
                                                            : <Icon />
                                                        }
                                                    </div>
                                                    {/* Info */}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">{item.label || item.name}</p>
                                                        <p className="text-xs text-gray-400 font-mono truncate">{item.slug}</p>
                                                    </div>
                                                    {/* Status pill */}
                                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${active
                                                        ? 'text-green-700 bg-green-100 dark:bg-green-500/20 dark:text-green-400'
                                                        : 'text-gray-500 bg-gray-100 dark:bg-gray-800'
                                                        }`}>
                                                        {active ? 'Active' : 'Inactive'}
                                                    </span>
                                                    {/* Actions */}
                                                    <div className="flex items-center gap-0.5 shrink-0">
                                                        <button
                                                            onClick={() => handleEditMode(item)}
                                                            className={`p-1.5 rounded-lg transition-colors ${isBeingEdited
                                                                ? 'text-brand-500 bg-brand-100 dark:bg-brand-500/20'
                                                                : 'text-gray-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10'
                                                                }`}
                                                            title="Edit"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteMode(item.id)}
                                                            className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                                            title="Delete"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/[0.02] sticky bottom-0 z-10 flex justify-between items-center">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 font-medium transition-colors"
                        >
                            {modalTab === 'manage' ? 'Done' : 'Cancel'}
                        </button>
                        {modalTab === 'configure' && (
                            <button
                                type="button"
                                onClick={handleSaveConfig}
                                disabled={configSaving}
                                className="px-6 py-2 text-sm text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-md shadow-brand-500/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {configSaving ? <><SpinnerIcon />Saving…</> : 'Save Configuration'}
                            </button>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Media Picker */}
            <MediaPickerModal
                isOpen={isMediaModalOpen}
                onClose={() => setIsMediaModalOpen(false)}
                onSelect={(file) => {
                    setModeForm((p) => ({ ...p, image_id: file.id, image_preview: file.path || file.url || '' }));
                    setIsMediaModalOpen(false);
                }}
            />
        </>
    );
}

/* ─── PaymentCard ─────────────────────────────────────────────────────────── */

interface PaymentCardProps {
    name: string;
    type: 'Gateway' | 'Mode';
    colorClass: string;
    icon: React.ReactNode;
}

function PaymentCard({ name, type, colorClass, icon }: PaymentCardProps) {
    return (
        <div className="flex flex-col gap-2.5 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all group">
            <div className="flex items-start justify-between">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                    {icon}
                </div>
                <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10 px-1.5 py-0.5 rounded-full">
                    Active
                </span>
            </div>
            <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-white truncate leading-tight">{name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{type}</p>
            </div>
        </div>
    );
}

/* ─── SectionCard ─────────────────────────────────────────────────────────── */

interface SectionCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    accent: 'brand' | 'gray';
    children: React.ReactNode;
}

function SectionCard({ icon, title, description, accent, children }: SectionCardProps) {
    const accentClass = accent === 'brand'
        ? 'bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400'
        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-white/[0.02]">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${accentClass}`}>{icon}</div>
                <div>
                    <p className="font-semibold text-gray-800 dark:text-white text-sm">{title}</p>
                    <p className="text-xs text-gray-500">{description}</p>
                </div>
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

/* ─── ModalTab ────────────────────────────────────────────────────────────── */

function ModalTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${active
                ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
        >
            {children}
        </button>
    );
}
